/** Detecta si el teléfono está corriendo una versión vieja de la app,
 *  SIN depender del service worker.
 *
 *  Por qué hace falta: todo el sistema de actualización normal pasa por
 *  el service worker, y si ese mecanismo se atasca —cosa que ya pasó—
 *  no hay forma de darse cuenta desde adentro: la app se ve bien,
 *  simplemente es vieja. Recargar tampoco ayuda, porque quien responde
 *  la recarga es el mismo service worker atascado.
 *
 *  La salida es preguntarle al servidor directamente. `version.json` no
 *  está en la lista de archivos que el service worker guarda, y además
 *  se pide con un parámetro distinto cada vez, así que la respuesta
 *  viene de la red de verdad. Si el sello que devuelve no es el mismo
 *  que quedó incrustado en el código que está corriendo, es que este
 *  código es viejo. */
export async function hayVersionMasNueva(): Promise<boolean> {
  try {
    const resp = await fetch(`/version.json?cb=${Date.now()}`, { cache: 'no-store' })
    if (!resp.ok) return false
    const datos = (await resp.json()) as { version?: string }
    return Boolean(datos.version) && datos.version !== __VERSION_BUILD__
  } catch {
    // Sin internet o servidor caído: no es momento de avisar nada.
    return false
  }
}

/** Deja el navegador sin nada guardado de la app —service worker y
 *  cachés— y recarga. Es lo mismo que hace el enlace `?limpiar=1`, pero
 *  sin tocar la sesión ni los datos locales: solo se bota lo que sirve
 *  archivos viejos.
 *
 *  Se usa como acción del botón "Actualizar" en vez de una recarga
 *  simple, porque una recarga simple la contesta el propio service
 *  worker: si es él el que está atascado, devuelve lo mismo de siempre y
 *  la persona vuelve a quedar igual. */
export async function aplicarVersionNueva() {
  try {
    if ('serviceWorker' in navigator) {
      const registros = await navigator.serviceWorker.getRegistrations()
      await Promise.all(registros.map((r) => r.unregister()))
    }
    if ('caches' in window) {
      const claves = await caches.keys()
      await Promise.all(claves.map((k) => caches.delete(k)))
    }
  } catch {
    // Si el navegador no deja limpiar, igual conviene recargar: puede
    // que con eso baste.
  } finally {
    // El parámetro obliga a una dirección distinta, para que ni el
    // navegador ni ningún intermediario puedan contestar con una copia
    // guardada de la página.
    window.location.replace(`/?v=${Date.now()}`)
  }
}
